import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Text from 'components/_UI/Text';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { useEvent } from 'context/events/Event';
import customFieldsApi from 'api/requests/customFields';
import randomPickerApi from 'api/requests/randomPicker';
import faqApi from 'api/requests/faq';
import pixelsApi from 'api/requests/pixels';
import certificateApi from 'api/requests/certificate';
import sponsorSectionsApi from 'api/requests/sponsorSections';
import campaignsApi from 'api/requests/campaigns';
import eventFeaturesApi from 'api/requests/eventFeatures';
import AdditionalHub from './hub/AdditionalHub';
import CustomFieldsSection from './sections/CustomFieldsSection';
import GiveawaySection from './sections/GiveawaySection';
import FaqSection from './sections/FaqSection';
import PixelsSection from './sections/PixelsSection';
import CertificateSection from './sections/CertificateSection';
import SponsorsSection from './sections/SponsorsSection';
import CampaignsSection from './sections/CampaignsSection';
import LocalSection from './sections/LocalSection';

export default function Additional() {
    const { toast, toasts } = useToast();
    const { event } = useEvent();

    const [activeSection, setActiveSection] = useState(null);

    const [loadingFields, setLoadingFields] = useState(false);
    const [loadingGiveaways, setLoadingGiveaways] = useState(false);
    const [loadingFaq, setLoadingFaq] = useState(false);
    const [loadingPixels, setLoadingPixels] = useState(false);
    const [loadingCertificate, setLoadingCertificate] = useState(false);
    const [loadingSponsorSections, setLoadingSponsorSections] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [loadingFeatures, setLoadingFeatures] = useState(false);

    const [content, setContent] = useState();
    const [giveaways, setGiveaways] = useState([]);
    const [faq, setFaq] = useState([]);
    const [pixels, setPixels] = useState([]);
    const [certificate, setCertificate] = useState(null);
    const [sponsorSections, setSponsorSections] = useState([]);
    const [campaigns, setCampaigns] = useState(null);
    const [features, setFeatures] = useState([]);

    const [perPage, setPerPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [firstPage, setFirstPage] = useState(0);

    useEffect(() => {
        getCustomFields();
        getGiveaways();
        getFaq();
        getPixels();
        getCertificate();
        getSponsorSections();
        getCampaigns();
        getFeatures();
    }, []);

    function refreshData() {
        getCustomFields();
        getGiveaways();
        getFaq();
        getPixels();
        getCertificate();
        getSponsorSections();
        getCampaigns();
        getFeatures();
    }

    async function getCustomFields() {
        setLoadingFields(true);
        try {
            const { data } = await customFieldsApi.get(event?.slug);
            setContent(data?.data);
        } catch (error) {
        } finally {
            setLoadingFields(false);
        }
    }

    async function getGiveaways(number = 1) {
        setLoadingGiveaways(true);
        try {
            const { data } = await randomPickerApi.get(event?.slug, number);
            setGiveaways(data?.data);
            setPerPage(data?.meta?.per_page);
            setFirstPage(data?.meta?.first_page);
            setLastPage(data?.meta?.last_page);
            setCurrentPage(data?.meta?.current_page);
            setTotalItems(data?.meta?.total);
        } catch (error) {
        } finally {
            setLoadingGiveaways(false);
        }
    }

    async function getFaq() {
        setLoadingFaq(true);
        try {
            const { data } = await faqApi.get(event?.slug);
            setFaq(data?.data);
        } catch (error) {
        } finally {
            setLoadingFaq(false);
        }
    }

    async function getPixels() {
        setLoadingPixels(true);
        try {
            const { data } = await pixelsApi.get(event?.slug);
            setPixels(data?.data || []);
        } catch (error) {
        } finally {
            setLoadingPixels(false);
        }
    }

    async function getCertificate() {
        setLoadingCertificate(true);
        try {
            const { data } = await certificateApi.get(event?.slug);
            setCertificate(data?.data || null);
        } catch (error) {
        } finally {
            setLoadingCertificate(false);
        }
    }

    async function getSponsorSections() {
        setLoadingSponsorSections(true);
        try {
            const { data } = await sponsorSectionsApi.get(event?.slug);
            setSponsorSections(data?.data || []);
        } catch (error) {
        } finally {
            setLoadingSponsorSections(false);
        }
    }

    async function getCampaigns() {
        setLoadingCampaigns(true);
        try {
            const { data } = await campaignsApi.get(event?.slug);
            setCampaigns(data?.data || null);
        } catch (error) {
        } finally {
            setLoadingCampaigns(false);
        }
    }

    async function getFeatures() {
        setLoadingFeatures(true);
        try {
            const { data } = await eventFeaturesApi.get(event?.slug);
            setFeatures(data?.data || []);
        } catch (error) {
        } finally {
            setLoadingFeatures(false);
        }
    }

    function handleBack() {
        setActiveSection(null);
    }

    const summaryData = { customFields: content, giveaways, faq, pixels, certificate, sponsorSections, campaigns, features };

    function renderSection() {
        switch (activeSection) {
            case 'customFields':
                return (
                    <CustomFieldsSection
                        data={content}
                        loading={loadingFields}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            case 'giveaway':
                return (
                    <GiveawaySection
                        data={giveaways}
                        loading={loadingGiveaways}
                        pagination={{ perPage, lastPage, currentPage, totalItems, firstPage }}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                        onPaginate={(number) => getGiveaways(number)}
                    />
                );
            case 'faq':
                return (
                    <FaqSection
                        data={faq}
                        loading={loadingFaq}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            case 'pixels':
                return (
                    <PixelsSection
                        data={pixels}
                        loading={loadingPixels}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            case 'campaigns':
                return (
                    <CampaignsSection
                        data={campaigns}
                        loading={loadingCampaigns}
                        onBack={handleBack}
                    />
                );
            case 'certificate':
                return (
                    <CertificateSection
                        data={certificate}
                        loading={loadingCertificate}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            case 'sponsors':
                return (
                    <SponsorsSection
                        data={sponsorSections}
                        loading={loadingSponsorSections}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            case 'local':
                return (
                    <LocalSection
                        data={features}
                        loading={loadingFeatures}
                        onBack={handleBack}
                        toast={toast}
                        onSuccess={refreshData}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <Container>
            <ToastContainer toasts={toasts} />

            <HeaderContent>
                <Column width='auto'>
                    <Text name='subtitle' color={colors.nightDark}>
                        Configurações adicionais
                    </Text>
                    <Text marginBottom={'10px'} paddingBottom={0}>
                        Personalize e estenda as funcionalidades do seu evento.
                    </Text>
                </Column>
            </HeaderContent>

            {activeSection === null ? (
                <AdditionalHub
                    summaryData={summaryData}
                    onSelectSection={setActiveSection}
                />
            ) : (
                renderSection()
            )}
        </Container>
    );
}

const Container = styled(Column)`
    width: 100%;
`;

const HeaderContent = styled(Column)`
    width: 100%;
    padding: 14px 0;

    & > button { margin-top: 20px; }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        & > h2,
        & > button {
            width: auto;
            margin-top: 0;
        }
    }
`;
